// useFacilityFieldUpdate — generic single-field mutation against any
// Info-tab table. Optimistic UI is the caller's responsibility (caller
// keeps its own local state, calls update(), rolls back on error).
//
// Returns a function that resolves to { ok, error } so the caller can
// react synchronously (rollback + toast).
//
// Special cases:
//   - facility_access has the unusual rule that the row may not exist yet.
//     If `row_id` is null on a facility_access update, this hook INSERTs
//     a stub row with the new value (Migration A's owner_insert RLS
//     allows this for the AM of the facility) and returns the new
//     access_id so the caller can swap to UPDATE on subsequent saves.
// ============================================================================

import { useCallback } from 'react';
import { supabase } from '../../../shared/supabase';
import { commitWrite, refreshReaders, WriteDidNotLandError } from '../../../shared/save-path';
import { columnLabel } from '../../../shared/column-label';
import { errorMessage } from '../../../shared/errors';

/** The sentence to put in front of the person, whatever threw. */
function describedMessage(err: unknown): string {
  if (err instanceof WriteDidNotLandError) return err.described.message;
  return errorMessage(err, 'Could not save that change.');
}

export type FieldUpdateTable =
  | 'facilities'
  | 'facility_access'
  | 'facility_details'
  | 'service_assignments'
  | 'contacts'
  // ⛔ A person and that person AT THIS BUILDING are two rows. `contacts` is who
  // they are everywhere; `contact_assignments` carries the title, phone, email
  // and role for one building, and 176 of 262 displayed assignments carry an
  // override today. Writing a per-building fact to the person master edits them
  // everywhere AND is invisible here, because the override still wins the read.
  | 'contact_assignments';

export interface FieldUpdateInput {
  table: FieldUpdateTable;
  row_id: string | null; // primary-key value; null only valid for facility_access INSERT-on-first-save
  pk_column: string; // e.g. 'access_id', 'facility_id', 'assignment_id', 'contact_id'
  field: string;
  value: string | number | boolean | null;
  // facility_access only: when row_id is null we INSERT and need to know the facility
  facility_id?: string;
}

export interface FieldUpdateResult {
  ok: boolean;
  error: string | null;
  // facility_access INSERT-on-first-save returns the new PK so the caller
  // can swap from "create" to "update" mode for the next save.
  new_row_id?: string;
}

export function useFacilityFieldUpdate() {
  const update = useCallback(
    async (input: FieldUpdateInput): Promise<FieldUpdateResult> => {
      const { table, row_id, pk_column, field, value, facility_id } = input;

      // facility_access INSERT-on-first-save when no row exists yet
      if (table === 'facility_access' && row_id == null) {
        if (!facility_id) {
          return { ok: false, error: 'facility_id required to create facility_access row' };
        }
        const insertPayload: Record<string, unknown> = { facility_id };
        insertPayload[field] = value;
        const { data, error } = await supabase
          .from('facility_access')
          .insert(insertPayload)
          .select('access_id')
          .maybeSingle();
        if (error) {
          return { ok: false, error: error.message };
        }
        // This branch already asked for the row back, so it can tell an insert
        // that landed from one that did not. Readers still have to be told.
        refreshReaders(['facility_access']);
        return { ok: true, error: null, new_row_id: (data as any)?.access_id ?? undefined };
      }

      // ⛔ A SERVICE LINE HAS ONE WRITER AND THIS IS NOT IT. save_service_line()
      // decides whether a money field is a line still being set up or a rate
      // change on a building that is already being served, and routes the second
      // through the one rate-change process so Finance hears about it. A generic
      // column update cannot tell those apart and would write a live rate
      // silently. It refuses per field, with its own sentence.
      if (table === 'service_assignments') {
        if (!facility_id) {
          return { ok: false, error: 'facility_id required to save a service line' };
        }
        try {
          const { data, error } = await supabase.rpc('save_service_line', {
            p_facility_id: facility_id,
            p_patch: { [field]: value },
            p_assignment_id: row_id,
            p_sop_id: null,
          });
          if (error) throw error;
          const refused = (data as { refused?: Record<string, string> } | null)?.refused ?? {};
          const sentences = Object.values(refused);
          if (sentences.length > 0) return { ok: false, error: sentences.join(' ') };
        } catch (err) {
          return { ok: false, error: describedMessage(err) };
        }
        refreshReaders(['service_assignments', 'facilities']);
        return { ok: true, error: null };
      }

      if (row_id == null) {
        return { ok: false, error: `row_id required for ${table} update` };
      }

      const payload: Record<string, unknown> = {};
      payload[field] = value;

      // ⛔ `.select(pk_column)` is not decoration. Without it PostgREST returns
      // success and a null body whether the update changed the row or matched
      // nothing at all — so a policy that permits the read and refuses the
      // write was indistinguishable from a save, and the caller (which owns the
      // optimistic value) had no reason to roll back. The user then sat looking
      // at a value the database never took. That is the shape behind the
      // worker-name ticket on a facility assignment, and `service_assignments`
      // is one of THIS hook's tables. A zero-row protected write is a failure,
      // not a no-op. commitWrite proves it and refreshes the readers.
      try {
        await commitWrite({
          label: `save ${columnLabel(field) ?? field}`,
          write: supabase.from(table).update(payload).eq(pk_column, row_id).select(pk_column),
          touches: [table],
        });
      } catch (err) {
        // 42501 = insufficient_privilege — the column guard's rejection. Its
        // RAISE EXCEPTION text is the most helpful thing we can show, and
        // describeError keeps it. The zero-row refusal arrives here too, with
        // its own sentence.
        return { ok: false, error: describedMessage(err) };
      }
      return { ok: true, error: null };
    },
    [],
  );

  // ==========================================================================
  // updateFields — a whole card in ONE statement.
  //
  // 2026-09-16 (info-tab-one-edit-door). The Info-tab cards used to save a
  // draft by looping `update()` once per field, which produced one PostgREST
  // round trip and one audit_log row per column, and made a PARTIAL failure
  // possible: some columns committed, the card reported failure, and the AM
  // reported "it doesn't save" while the database already held half of it.
  // That is the shape saveDraftUnits was written to REPORT; one statement
  // makes it impossible to have.
  //
  // One card, one Save, one write, one audit row naming every column together.
  // ⛔ service_assignments still routes through save_service_line(), which is
  // the only writer that can tell a line being set up from a rate change on a
  // building that is already being served. It takes the whole patch and
  // refuses per field, so the window can say exactly which fields it kept.
  // ==========================================================================
  // The body is saveFieldsOnce below, so Ask AI's building card saves through
  // exactly this door (TCSD-0188, item 944) without a hook.
  const updateFields = useCallback(saveFieldsOnce, []);

  return { update, updateFields };
}

export interface SaveFieldsInput {
  table: FieldUpdateTable;
  row_id: string | null;
  pk_column: string;
  fields: Record<string, string | number | boolean | null>;
  /** The person's words for what they were doing — it goes in the message. */
  label: string;
  facility_id?: string;
}

/**
 * A whole Info-tab card in ONE statement — the save every Info-tab card makes
 * (useFacilityFieldUpdate().updateFields is this function). Exported so Ask AI's
 * building card calls the same door the screen does.
 */
export async function saveFieldsOnce(input: SaveFieldsInput): Promise<FieldUpdateResult> {
  const { table, row_id, pk_column, fields, label, facility_id } = input;
  if (Object.keys(fields).length === 0) return { ok: true, error: null };

  if (table === 'service_assignments') {
    if (!facility_id) {
      return { ok: false, error: 'facility_id required to save a service line' };
    }
    try {
      const { data, error } = await supabase.rpc('save_service_line', {
        p_facility_id: facility_id,
        p_patch: fields,
        p_assignment_id: row_id,
        p_sop_id: null,
      });
      if (error) throw error;
      const refused = (data as { refused?: Record<string, string> } | null)?.refused ?? {};
      const sentences = Object.entries(refused).map(
        ([field, why]) => `${columnLabel(field) ?? field} — ${why}`,
      );
      if (sentences.length > 0) return { ok: false, error: sentences.join(' ') };
    } catch (err) {
      return { ok: false, error: describedMessage(err) };
    }
    refreshReaders(['service_assignments', 'facilities']);
    return { ok: true, error: null };
  }

  // facility_access may not have a row yet — the first save creates it.
  if (table === 'facility_access' && row_id == null) {
    if (!facility_id) {
      return { ok: false, error: 'facility_id required to create facility_access row' };
    }
    const { data, error } = await supabase
      .from('facility_access')
      .insert({ facility_id, ...fields })
      .select('access_id')
      .maybeSingle();
    if (error) return { ok: false, error: error.message };
    refreshReaders(['facility_access']);
    return { ok: true, error: null, new_row_id: (data as any)?.access_id ?? undefined };
  }

  if (row_id == null) {
    return { ok: false, error: `row_id required for ${table} update` };
  }

  try {
    await commitWrite({
      label,
      write: supabase.from(table).update(fields).eq(pk_column, row_id).select(pk_column),
      touches: [table],
    });
  } catch (err) {
    return { ok: false, error: describedMessage(err) };
  }
  return { ok: true, error: null };
}
